import { SxProps, Theme } from '@mui/material/styles';

import React, { useEffect, useState } from 'react';

import { AiCallForCallPopDTO, Company, isPointmanCompany } from '_lib/api/apiTypes.shim';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { Stack } from '_lib/components/Stack/Stack';

import { TabPanel } from '_lib/components/TabPanel/TabPanel';
import { ThreeTabFooter } from '_lib/featureComponents/ThreeTabFooter/ThreeTabFooter';
import { TwoTabFooter } from '_lib/featureComponents/TwoTabFooter/TwoTabFooter';
import { useMediaQuery } from '_lib/hooks/useMediaQuery';
import { useAppDispatch } from '_lib/redux/hooks';
import { VerticalDivider } from '_lib/styles/PhoneCall.styled';
import { PhoneCallURLState } from '_lib/types/PhoneCall.types';
import { logDebug } from '_lib/utils/logging';

import { PointmanCompanyContext } from '../../common/CompanyPane/Company.slice';
import { CompanyPane } from '../../common/CompanyPane/CompanyPane';

import { CompanyZoneZipSelect } from './featureComponents/CompanyPane/CompanyZoneZipSelect/CompanyZoneZipSelect';
import { PointmanCallDetails } from './featureComponents/PointmanCallDetails/PointmanCallDetails';
import { PointmanCustomerPane } from './featureComponents/PointmanCustomerPane/PointmanCustomerPane';
import { normalizeTradeFromCompany, setPartialPhoneCallContext } from './PointmanPhoneCall.slice';

type PointmanPhoneCallProps = {
  companyContext: PointmanCompanyContext;
  phoneCallURLState: PhoneCallURLState;
  aiCall: AiCallForCallPopDTO['aiCall'] | null;
  handleSwitchCompany: (companyId: string) => Promise<Company | null>;
};

export const PointmanPhoneCall = (props: PointmanPhoneCallProps): React.ReactElement => {
  const { companyContext, phoneCallURLState, aiCall, handleSwitchCompany } = props;

  const dispatch = useAppDispatch();

  const handleSwitchPointmanCompany = async (companyId: string) => {
    logDebug('// - Company Switch Handler Pointman - //');
    const newCompany = await handleSwitchCompany(companyId);
    if (!newCompany || !isPointmanCompany(newCompany)) return;
    dispatch(normalizeTradeFromCompany(newCompany));
    // Customers are scoped per company, so need to clear it out
    dispatch(
      setPartialPhoneCallContext({
        updates: { customer: null }
      })
    );
  };

  const [tabIndex, setTabIndex] = useState(1); // default to customer pane shown if in tabbed view
  const [isSaving, setIsSaving] = useState(false);

  // If 'lg' or smaller, render with customer and company tabs
  const twoTabView = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  // If 'md' or smaller, customer, call, and company are all tabbed
  const threeTabView = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const generateCallDetailsTabSx = (): SxProps<Theme> => {
    if (threeTabView) return { width: '100%', height: '100%' };
    if (twoTabView) return { width: '20%', minWidth: '330px', height: '100%' };
    return { width: '20%', height: '100%', minWidth: '330px' };
  };

  // customer/company have same width/height
  const generateCustomerAndCompanyTabSx = (): SxProps<Theme> => {
    if (threeTabView) return { width: '100%', height: '100%' };
    if (twoTabView) return { width: '80%', height: '100%' };
    return { width: '40%', height: '100%' };
  };

  // We want to auto-select 'customer' if we had 'call' selected and then left the breakpoint width
  useEffect(() => {
    if (twoTabView && !threeTabView && tabIndex === 0) setTabIndex(1);
  }, [twoTabView, threeTabView, tabIndex]);

  return (
    <FlexBox sx={{ height: '100vh', width: '100%', flexDirection: 'column', overflow: 'hidden' }}>
      <BackdropSpinner open={isSaving} />
      <Stack direction={'row'} spacing={0} sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
        <TabPanel currentIndex={tabIndex} index={0} alwaysDisplay={!threeTabView} sx={generateCallDetailsTabSx()}>
          <PointmanCallDetails
            companyContext={companyContext}
            phoneCallURLState={phoneCallURLState}
            aiCallId={aiCall?.id}
            onSetIsSaving={setIsSaving}
            showFooter={!twoTabView || threeTabView}
          />
        </TabPanel>
        <VerticalDivider />
        <TabPanel
          currentIndex={tabIndex}
          index={1}
          alwaysDisplay={!twoTabView && !threeTabView}
          sx={generateCustomerAndCompanyTabSx()}>
          <PointmanCustomerPane
            phoneCallURLState={phoneCallURLState}
            company={companyContext.company}
            onSetIsSaving={setIsSaving}
          />
        </TabPanel>
        <VerticalDivider />
        <TabPanel
          currentIndex={tabIndex}
          index={2}
          alwaysDisplay={!twoTabView && !threeTabView}
          sx={generateCustomerAndCompanyTabSx()}>
          <CompanyPane
            zipZoneOptions={{ ZipZoneSelectComponent: CompanyZoneZipSelect }}
            companyContext={companyContext}
            phoneCallURLState={phoneCallURLState}
            aiCall={aiCall}
            handleSwitchCompany={handleSwitchPointmanCompany}
            sx={{ visibility: 'visible', width: '100%', height: '100%' }}
          />
        </TabPanel>
      </Stack>
      {twoTabView && !threeTabView && (
        <TwoTabFooter callDetailsWidth='30%' tabIndex={tabIndex} setTabIndex={setTabIndex} />
      )}
      {threeTabView && <ThreeTabFooter tabIndex={tabIndex} setTabIndex={setTabIndex} />}
    </FlexBox>
  );
};
